import { z } from 'zod';

export const StockWavelengthSchema = z.object({
  um: z.number(),
  weight: z.number(),
  /** OpticStudio's own IWavelength.IsPrimary flag. */
  primary: z.boolean(),
});
export type StockWavelength = z.infer<typeof StockWavelengthSchema>;

/** One indexed stock part. `eflMm`/`epdMm`/`shape` are nullable because the
 *  catalog encodes "no optical power", "not reported" and "unknown shape" as
 *  sentinel values that are normalized away at index time.
 *
 *  The lens-file fields (migration 073) are nullable for a different reason:
 *  they exist only once the build has OPENED the part, and a metadata-only
 *  build never does. */
export const StockPartSchema = z.object({
  source: z.string(),
  vendor: z.string(),
  partNumber: z.string(),
  eflMm: z.number().nullable(),
  epdMm: z.number().nullable(),
  shape: z.string().nullable(),
  lensType: z.string(),
  nElements: z.number(),
  telecentric: z.boolean(),
  /** SystemData.TitleNotes.Title, e.g. '45805 Achromatic Lens'. */
  title: z.string().nullable(),
  /** SystemData.TitleNotes.Notes — the vendor's free-text description. */
  description: z.string().nullable(),
  /** The lens file's full wavelength table, verbatim. Kept whole because
   *  collapsing it discards real data (Edmund 12159 is a 1.064 µm part carrying
   *  a 0.6328 µm alignment wavelength at weight 0.2). */
  wavelengths: z.array(StockWavelengthSchema).nullable(),
  /** The entry whose IsPrimary is true — projected, not chosen. */
  primaryWavelengthUm: z.number().nullable(),
  /** Distinct glass names in surface order (air excluded). */
  materials: z.array(z.string()).nullable(),
  /** Distinct coating names in surface order. */
  coatings: z.array(z.string()).nullable(),
});
export type StockPart = z.infer<typeof StockPartSchema>;

export const StockPartVendorFacetSchema = z.object({
  vendor: z.string(),
  count: z.number(),
});

export const StockPartSearchSchema = z.object({
  rows: z.array(StockPartSchema),
  total: z.number(),
  vendorFacets: z.array(StockPartVendorFacetSchema),
  /** False when the index has never been built — the UI must say so rather
   *  than render an empty result set as "no matches". */
  indexBuilt: z.boolean(),
  sourceRelease: z.string().nullable(),
  indexedAt: z.string().nullable(),
});
export type StockPartSearch = z.infer<typeof StockPartSearchSchema>;

const csv = z.string().transform((s) => s.split(',').filter(Boolean));

/** GET /stock-parts/search's query string. The route scopes `source` to its default catalog when it is absent. */
export const StockPartSearchQuerySchema = z.object({
  source: z.string().optional(),
  vendors: csv.optional(),
  shapes: csv.optional(),
  types: csv.optional(),
  eflMin: z.coerce.number().optional(),
  eflMax: z.coerce.number().optional(),
  epdMin: z.coerce.number().optional(),
  epdMax: z.coerce.number().optional(),
  nElements: z.coerce.number().int().positive().optional(),
  eflTarget: z.coerce.number().optional(),
  // Design waveband (µm) over the lens file's own primary wavelength.
  wavelengthUmMin: z.coerce.number().positive().optional(),
  wavelengthUmMax: z.coerce.number().positive().optional(),
  // Bounded here; NUL-stripping happens in the backend's buildStockPartsQuery,
  // which both the route and the agent tool go through. Measured 2026-07-25:
  // ~1 MB of text raises 54001 after ~10 s of DB CPU, so the length cap is the
  // part that has to be a rejection rather than a sanitization.
  text: z.string().max(200).optional(),
  // z.stringbool (not z.coerce.boolean, which is Boolean(value) and maps
  // "false" → true) so ?includeWindows=false actually excludes windows.
  includeWindows: z.stringbool({ truthy: ['true'], falsy: ['false'] }).default(false),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type StockPartSearchQuery = z.output<typeof StockPartSearchQuerySchema>;

/** The search filters: the query without its paging. */
export type StockPartFilter = Omit<StockPartSearchQuery, 'limit' | 'offset'>;
